import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    DataSourceAbstract,
    Field,
    FilterableProtocol,
    FilterBlockAbstract,
    FilterFormType,
    FilterMode,
    IndependentAction,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    ViewField,
    ViewType
} from '@lighthouse/core'
import isEqual from 'fast-deep-equal'
import { clone, isEmpty } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useUpdateEffect } from 'react-use'

import type { FlowNode, Workflow } from '../../types'
import { FilterGroup } from './FilterGroup'
import * as SC from './styles'

interface FilterContentProps {
    children?: React.ReactNode
    filter?: FilterableProtocol['filter']
    dataSource?: DataSourceAbstract
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    dataSourceList: DataSourceAbstract[]
    workflowList?: Workflow[]
    pageVariables?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    pageStateParameter?: StateSchema[]
    upstreamNodes?: FlowNode[]
    allUpstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    columns: ViewField[]
    primaryField?: string
    noSettingInnerType?: Set<TypeInstanceMap>
    fieldMode?: FilterMode
    paramsMode?: FilterMode
    enableCurrentUser?: boolean
    formDsId?: string
    viewDsId?: string
    viewType?: ViewType
    extraViewFields?: Field[]
    paramsDataSource?: DataSourceAbstract
    isUseImage?: boolean
    filterBlocks?: FilterBlockAbstract[]
    enableFilterVariable?: boolean
    disabled?: boolean
    onFilter?: (val: FilterFormType) => void
    onMouseEnter?: (blockId: string) => void
    onMouseLeave?: () => void
}

export const FilterContent: React.FC<FilterContentProps> = ({
    filter,
    dataSource,
    availableFieldBlocksInfo,
    availableFilterBlocksInfo,
    dataSourceList,
    workflowList,
    pagePathParameter,
    pageQueryParameter,
    pageStateParameter,
    pageVariables,
    upstreamNodes,
    allUpstreamNodes,
    allParentActions,
    columns,
    primaryField,
    noSettingInnerType,
    fieldMode,
    paramsMode,
    enableCurrentUser,
    formDsId,
    viewDsId,
    viewType,
    extraViewFields,
    paramsDataSource,
    isUseImage,
    filterBlocks,
    enableFilterVariable,
    disabled,
    onMouseEnter,
    onMouseLeave,
    onFilter
}) => {
    const filtersValue: FilterFormType = useMemo(() => {
        if (isEmpty(filter) || !filter) {
            return {
                expression: {
                    where: 'AND',
                    conditions: []
                }
            }
        }
        if (filter.expression && !filter.expression?.where) {
            return {
                expression: {
                    where: 'AND',
                    conditions: filter.expression?.conditions || []
                }
            }
        }
        return filter
    }, [filter])
    const methods = useForm<FilterFormType>({
        mode: 'onChange',
        defaultValues: filtersValue
    })

    const { control } = methods

    const handleFilter = useCallback(
        (data: FilterableProtocol['filter']) => {
            if (data) {
                onFilter?.(clone(data))
            }
        },
        [onFilter]
    )

    const formData = useWatch({ control })

    // const debouncedFormData = useDebounce(formData, 500)

    useUpdateEffect(() => {
        if (formData && !isEqual(formData, filter)) {
            handleFilter(formData || {})
        }
    }, [formData])

    return (
        <SC.PropertySettingWrapper>
            <FormProvider {...methods}>
                <SC.FilterForm>
                    <SC.PropertySettingTitle>筛选条件</SC.PropertySettingTitle>
                    <FilterGroup
                        columns={columns}
                        dataSource={dataSource}
                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                        pagePathParameter={pagePathParameter}
                        pageQueryParameter={pageQueryParameter}
                        pageVariables={pageVariables}
                        pageStateParameter={pageStateParameter}
                        upstreamNodes={upstreamNodes}
                        allUpstreamNodes={allUpstreamNodes}
                        allParentActions={allParentActions}
                        dataSourceList={dataSourceList}
                        workflowList={workflowList}
                        primaryField={primaryField}
                        prefixName="expression"
                        fieldMode={fieldMode}
                        paramsMode={paramsMode}
                        enableCurrentUser={enableCurrentUser}
                        noSettingInnerType={noSettingInnerType}
                        formDsId={formDsId}
                        viewDsId={viewDsId}
                        viewType={viewType}
                        extraViewFields={extraViewFields}
                        paramsDataSource={paramsDataSource}
                        isUseImage={isUseImage}
                        disabled={disabled}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        filterBlocks={filterBlocks}
                        enableFilterVariable={enableFilterVariable}
                    />
                </SC.FilterForm>
            </FormProvider>
        </SC.PropertySettingWrapper>
    )
}
